import { useCallback, useEffect, useRef, useState } from "react";
import { GripVertical, LayoutGrid, Loader2, Plus, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Panel, PrimaryButtonClass } from "@/components/common/Chrome";
import { RangeSelect } from "@/components/dash/DashKit";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/format";
import { EDIT_SPAN_CLASS, clampSpan, clampRows } from "@/lib/gridSpan";

const HEIGHT_PRESETS = [
  { rows: 1, label: "Auto", hint: "Each card keeps its natural height" },
  { rows: 2, label: "Short", hint: "Every card locked to 380px rows" },
  { rows: 3, label: "Medium", hint: "Every card locked to 560px rows" },
  { rows: 4, label: "Tall", hint: "Every card locked to 740px rows" },
];

const GROUP_TONE = {
  Tiles: "bg-primary/10 text-primary border-primary/25",
  Charts: "bg-[hsl(var(--teal))]/12 text-[hsl(var(--teal))] border-[hsl(var(--teal))]/25",
  Sections: "bg-amber-500/10 text-amber-600 border-amber-500/25 dark:text-amber-400",
  "Whole charity": "bg-violet-500/10 text-violet-600 border-violet-500/25 dark:text-violet-400",
};

export const OverviewLayoutSettings = () => {
  const { can } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const [resizing, setResizing] = useState(null);
  const canvasRef = useRef(null);
  const resizeRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/overview-layout");
    setState(data);
  }, []);

  useEffect(() => { load().catch(() => setState(false)); }, [load]);

  useEffect(() => {
    const move = (e) => {
      const r = resizeRef.current;
      if (!r) return;
      if (r.axis === "y") {
        const rows = clampRows(r.startRows + (e.clientY - r.startY) / 44);
        setState((s) => (s ? { ...s, heights: { ...s.heights, [r.key]: rows } } : s));
        return;
      }
      const colWidth = r.canvasWidth / 12;
      const span = clampSpan(r.startSpan + (e.clientX - r.startX) / colWidth);
      setState((s) => (s ? { ...s, spans: { ...s.spans, [r.key]: span } } : s));
    };
    const up = () => { resizeRef.current = null; setResizing(null); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);

  if (state === false) {
    return (
      <Panel testId="panel-overview-layout" kicker="Overview page" title="Arrange the dashboard">
        <div className="flex items-center justify-between gap-3 p-4">
          <p className="text-[12px] text-muted-foreground">The saved layout could not be loaded.</p>
          <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]"
                  data-testid="overview-layout-retry"
                  onClick={() => { setState(null); load().catch(() => setState(false)); }}>
            <RotateCcw className="mr-1 h-3 w-3" /> Try again
          </Button>
        </div>
      </Panel>
    );
  }
  if (!state) return null;
  const editable = can("user", "write");
  const meta = (key) => state.catalog.find((w) => w.key === key) || { label: key, group: "" };
  const spanOf = (key) => clampSpan(state.spans?.[key] ?? meta(key).span ?? 6);
  const rowsOf = (key) => clampRows(state.heights?.[key] ?? 1);

  const visible = state.order.filter((k) => state.widgets[k] !== false);
  const hidden = state.order.filter((k) => state.widgets[k] === false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/overview-layout", {
        widgets: state.widgets, order: state.order, spans: state.spans, heights: state.heights,
        default_range: state.default_range,
      });
      toast.success("Overview layout saved for everyone in this tenant");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const dropOn = (key) => {
    setOverKey(null);
    if (!dragKey || dragKey === key) return;
    const order = [...state.order];
    order.splice(order.indexOf(key), 0, order.splice(order.indexOf(dragKey), 1)[0]);
    setState({ ...state, order });
    setDragKey(null);
  };

  const startResize = (key, axis, e) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      key, axis, startX: e.clientX, startY: e.clientY,
      startSpan: spanOf(key), startRows: rowsOf(key),
      canvasWidth: canvasRef.current?.getBoundingClientRect().width || 1200,
    };
    setResizing(key);
  };

  const setVisible = (key, on) => {
    const order = on ? [...state.order.filter((k) => k !== key), key] : state.order;
    setState({ ...state, order, widgets: { ...state.widgets, [key]: on } });
  };

  const setRows = (key, rows) =>
    setState((s) => ({ ...s, heights: { ...s.heights, [key]: clampRows(rows) } }));

  const applyHeightPreset = (rows) => {
    const heights = { ...state.heights };
    state.catalog.forEach(({ key, group }) => { if (group !== "Tiles") heights[key] = rows; });
    setState({ ...state, heights });
  };

  const activePreset = (() => {
    const cards = visible.filter((k) => meta(k).group !== "Tiles");
    if (cards.length === 0) return null;
    const first = rowsOf(cards[0]);
    return cards.every((k) => rowsOf(k) === first) ? first : null;
  })();

  const reset = () => setState({
    ...state,
    order: state.catalog.map((w) => w.key),
    widgets: Object.fromEntries(state.catalog.map((w) => [w.key, true])),
    spans: Object.fromEntries(state.catalog.map((w) => [w.key, w.span])),
    heights: Object.fromEntries(state.catalog.map((w) => [w.key, 1])),
  });

  return (
    <Panel testId="panel-overview-layout" kicker="Overview page"
           title="Arrange the dashboard — drag to reorder, pull the edge to resize"
           actions={
             <div className="flex items-center gap-2">
               <span className="text-[11px] text-muted-foreground" data-testid="overview-visible-count">
                 {visible.length}/{state.catalog.length} on the page
               </span>
               {editable && (
                 <>
                   <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]"
                           data-testid="overview-layout-reset" onClick={reset}>
                     <RotateCcw className="mr-1 h-3 w-3" /> Reset
                   </Button>
                   <Button size="sm" onClick={save} disabled={busy} data-testid="overview-layout-save"
                           className={`h-8 text-[11px] ${PrimaryButtonClass}`}>
                     {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <LayoutGrid className="mr-1 h-3 w-3" />}
                     Save layout
                   </Button>
                 </>
               )}
             </div>
           }>
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 p-3">
          <div>
            <div className="text-[12.5px] font-semibold text-foreground">Default chart period</div>
            <div className="text-[11.5px] text-muted-foreground">
              Every dashboard chart opens on this window; viewers can still switch it themselves.
            </div>
          </div>
          <RangeSelect value={state.default_range} testId="overview-default-range"
                       onChange={(r) => editable && setState({ ...state, default_range: r })} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 p-3"
             data-testid="overview-height-presets">
          <div>
            <div className="text-[12.5px] font-semibold text-foreground">Row height preset</div>
            <div className="text-[11.5px] text-muted-foreground">
              Snap every chart and section card to one height so the rows line up on any screen.
              KPI tiles keep their natural height.
            </div>
          </div>
          <div className="flex items-center gap-0.5 rounded-lg border border-border p-0.5">
            {HEIGHT_PRESETS.map((p) => (
              <button key={p.rows} type="button" disabled={!editable}
                      data-testid={`overview-height-preset-${p.rows}`}
                      onClick={() => applyHeightPreset(p.rows)}
                      title={p.hint}
                      className={`rounded-md px-2 py-0.5 text-[10.5px] font-semibold transition-colors duration-150 disabled:opacity-50 ${
                        activePreset === p.rows
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-[11.5px] text-muted-foreground">
          This is a scale model of the Overview page on a wide screen. Drag a card onto another to
          change the order, grab the right edge to make it wider or narrower, grab the bottom edge to
          make it taller, and use the cross to take it off the page.
        </p>

        <div ref={canvasRef}
             data-testid="overview-canvas"
             className="grid grid-cols-12 gap-2 rounded-2xl border border-dashed border-border bg-muted/25 p-3">
          {visible.length === 0 && (
            <p className="col-span-12 py-6 text-center text-[12px] text-muted-foreground">
              Nothing on the page. Add a card from the tray below.
            </p>
          )}
          {visible.map((key) => {
            const m = meta(key);
            const span = spanOf(key);
            return (
              <div key={key}
                   draggable={editable && !resizing}
                   onDragStart={() => setDragKey(key)}
                   onDragEnd={() => { setDragKey(null); setOverKey(null); }}
                   onDragOver={(e) => { e.preventDefault(); setOverKey(key); }}
                   onDragLeave={() => setOverKey((k) => (k === key ? null : k))}
                   onDrop={() => dropOn(key)}
                   data-testid={`overview-card-${key}`}
                   style={{ height: 74 + (rowsOf(key) - 1) * 44 }}
                   className={`group relative flex select-none flex-col justify-between rounded-xl border p-2.5 transition-colors duration-150 ${
                     EDIT_SPAN_CLASS[span]
                   } ${GROUP_TONE[m.group] || "border-border bg-card"} ${
                     dragKey === key ? "opacity-40" : ""
                   } ${overKey === key ? "ring-2 ring-primary" : ""} ${
                     resizing === key ? "ring-2 ring-primary" : ""
                   }`}>
                <div className="flex items-start gap-1.5">
                  <GripVertical className={`mt-0.5 h-3.5 w-3.5 shrink-0 opacity-50 ${editable ? "cursor-grab" : ""}`} />
                  <span className="line-clamp-2 text-[11.5px] font-semibold leading-tight">{m.label}</span>
                  {editable && (
                    <button onClick={() => setVisible(key, false)}
                            data-testid={`overview-remove-${key}`}
                            className="ml-auto rounded p-0.5 opacity-0 transition-opacity duration-150 hover:bg-background/60 group-hover:opacity-100">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="flex items-end justify-between gap-1">
                  <span className="text-[10px] uppercase tracking-wider opacity-70">{m.group}</span>
                  <span className="flex items-center gap-1">
                    {editable && m.group !== "Tiles" && (
                      <span className="hidden items-center gap-0.5 group-focus-within:inline-flex group-hover:inline-flex">
                        {[1, 2, 3, 4].map((n) => (
                          <button key={n} type="button"
                                  data-testid={`overview-rows-${key}-${n}`}
                                  onClick={() => setRows(key, n)}
                                  title={HEIGHT_PRESETS[n - 1].hint}
                                  className={`rounded px-1 text-[9.5px] font-semibold leading-4 transition-colors duration-150 ${
                                    rowsOf(key) === n
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-background/60 opacity-70 hover:opacity-100"
                                  }`}>
                            {n}h
                          </button>
                        ))}
                      </span>
                    )}
                    <span className="font-mono text-[10px] opacity-80" data-testid={`overview-span-${key}`}>
                      {span}/12 · {rowsOf(key)}h
                    </span>
                  </span>
                </div>
                {editable && (
                  <>
                    <span onMouseDown={(e) => startResize(key, "x", e)}
                          data-testid={`overview-resize-${key}`}
                          title="Drag to make it wider or narrower"
                          className="absolute inset-y-1 right-0 flex w-3 cursor-col-resize items-center justify-center rounded-r-xl">
                      <span className="h-6 w-[3px] rounded-full bg-current opacity-30 transition-opacity duration-150 group-hover:opacity-70" />
                    </span>
                    <span onMouseDown={(e) => startResize(key, "y", e)}
                          data-testid={`overview-resize-v-${key}`}
                          title="Drag to make it taller or shorter"
                          className="absolute inset-x-1 bottom-0 flex h-3 cursor-row-resize items-center justify-center rounded-b-xl">
                      <span className="h-[3px] w-6 rounded-full bg-current opacity-30 transition-opacity duration-150 group-hover:opacity-70" />
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-border p-3" data-testid="overview-hidden-tray">
          <div className="mb-2 text-[12px] font-semibold text-foreground">
            Off the page ({hidden.length})
          </div>
          {hidden.length === 0 ? (
            <p className="text-[11.5px] text-muted-foreground">Every card is on the Overview page.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {hidden.map((key) => (
                <button key={key} disabled={!editable} onClick={() => setVisible(key, true)}
                        data-testid={`overview-add-${key}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11.5px] text-foreground transition-colors duration-150 hover:border-primary/50 hover:text-primary disabled:opacity-50">
                  <Plus className="h-3 w-3" /> {meta(key).label}
                </button>
              ))}
            </div>
          )}
        </div>

        {state.updated_by && (
          <p className="text-[11px] text-muted-foreground" data-testid="overview-layout-audit">
            Last changed by {state.updated_by} · {dateTime(state.updated_at)}
          </p>
        )}
        {!editable && (
          <p className="text-[11px] text-muted-foreground">
            Only a tenant administrator can change what the Overview page shows.
          </p>
        )}
      </div>
    </Panel>
  );
};
